import { ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";

// Served at a stable URL by the /assets/:file route (not bundler imports) so the
// src is identical in the client bundle and server-rendered HTML. The -480 and
// -960 copies are pre-resized (same 1250x966 aspect) and listed in build.ts
// ASSET_FILES; the picture never renders wider than 460px.
const how1Webp = "/assets/how-1.webp";
const how2Webp = "/assets/how-2.webp";

function howSrcSet(src: string): string {
  const base = src.replace(/\.webp$/, "");
  return `${base}-480.webp 480w, ${base}-960.webp 960w, ${src} 1250w`;
}

type Step = {
  numberKey: string;
  titleKey: string;
  descKey: string;
};

// Four steps, two illustrations: each picture carries the pair of steps it
// shows. Choosing a person and a time sit with the therapists' portraits,
// booking and joining sit with the video call at home.
type StepGroup = {
  img: string;
  altKey: string;
  steps: [Step, Step];
};

function step(n: 1 | 2 | 3 | 4): Step {
  return {
    numberKey: `home.steps.step${n}Number`,
    titleKey: `home.steps.step${n}Title`,
    descKey: `home.steps.step${n}Desc`,
  };
}

const GROUPS: [StepGroup, StepGroup] = [
  { img: how1Webp, altKey: "home.steps.step1Title", steps: [step(1), step(2)] },
  { img: how2Webp, altKey: "home.steps.step4Title", steps: [step(3), step(4)] },
];

function StepGroupRow({ group, t }: { group: StepGroup; t: (k: string) => string }) {
  return (
    <div className="grid items-center gap-8 sm:gap-12 lg:grid-cols-2 lg:gap-20">
      {/* Illustration (always left on desktop) */}
      <div className="order-1">
        <img
          src={group.img}
          srcSet={howSrcSet(group.img)}
          sizes="(min-width: 492px) 460px, calc(100vw - 32px)"
          width={1250}
          height={966}
          alt={t(group.altKey)}
          className="mx-auto h-auto w-full max-w-[460px]"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Copy */}
      <div className="order-2 flex flex-col gap-10 text-center sm:gap-12 lg:text-left">
        {group.steps.map((s) => (
          <div key={s.numberKey}>
            <span
              className="font-heading block text-5xl font-black leading-none sm:text-6xl"
              style={{ color: "oklch(74.72% 0.089 82.39)" }}
            >
              {t(s.numberKey)}
            </span>
            <h3 className="font-heading mt-4 text-2xl font-bold tracking-[-0.01em] text-foreground sm:text-3xl">
              {t(s.titleKey)}
            </h3>
            <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
              {t(s.descKey)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorksView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section ref={ref} className="animate-on-scroll relative overflow-hidden py-16 sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            {t("home.steps.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("home.steps.subtitle")}
          </p>
        </div>

        {/* Step groups with a connecting down arrow */}
        <div className="mt-14 sm:mt-20">
          <StepGroupRow group={GROUPS[0]} t={t} />

          <div className="my-10 flex justify-center sm:my-14" aria-hidden>
            <span
              className="flex size-12 items-center justify-center rounded-full"
              style={{ background: "oklch(37.12% 0.059 186.62 / 0.08)" }}
            >
              <ArrowDown className="size-5 text-primary" />
            </span>
          </div>

          <StepGroupRow group={GROUPS[1]} t={t} />
        </div>
      </div>
    </section>
  );
}

export const HowItWorks = defineSection({ Component: HowItWorksView });
